/**
 * Kanban-style matching history: one column per JD, CV cards beneath.
 * Owns search + pagination state; fetches via useJobMappingsByJDQuery.
 */

import { useState } from 'react';
import { FileText, Loader2, AlertCircle, User, Search, ChevronLeft, ChevronRight, Trash2 } from 'lucide-react';
import { useDebounce } from '@aaas/shared';
import { useTranslation } from 'react-i18next';
import { useJobMappingsByJDQuery, useDeleteMappingMutation, useTriggerAnalysisMutation } from '../../hooks/useAnalysisQueries';
import { AnalysisDetailPanel } from './AnalysisDetailPanel';
import type { AnalysisPanelState } from './AnalysisDetailPanel';
import type { AnalysisStatus, CVMappingInJD, JDWithMappings } from '../../types';

const PAGE_SIZE = 5;

// ── Status badge ──────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: AnalysisStatus }) {
  const { t } = useTranslation('ats');
  const cfg = {
    analyzing: { label: t('matching.status.analyzing'), className: 'bg-blue-50 text-blue-600', icon: <Loader2 className="h-3 w-3 animate-spin" /> },
    completed:  { label: t('matching.status.done'),      className: 'bg-green-50 text-green-700', icon: null },
    pending:    { label: t('matching.status.pending'),   className: 'bg-gray-100 text-gray-500',  icon: null },
    error:      { label: t('matching.status.error'),     className: 'bg-red-50 text-red-600',     icon: <AlertCircle className="h-3 w-3" /> },
  } as const;

  const c = cfg[status] ?? cfg.pending;
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs font-medium ${c.className}`}>
      {c.icon}
      {c.label}
    </span>
  );
}

// ── Score badge ───────────────────────────────────────────────────────────────

function ScoreBadge({ score }: { score: number | null }) {
  if (score === null) return null;

  const { bg, text, bar } =
    score >= 80 ? { bg: 'bg-green-50',  text: 'text-green-700',  bar: 'bg-green-400' } :
    score >= 60 ? { bg: 'bg-yellow-50', text: 'text-yellow-700', bar: 'bg-yellow-400' } :
                  { bg: 'bg-red-50',    text: 'text-red-700',    bar: 'bg-red-400' };

  return (
    <div className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-lg ${bg}`}>
      <span className={`text-xs font-bold tabular-nums ${text}`}>{score}%</span>
      <div className="w-8 h-1 rounded-full bg-gray-200 overflow-hidden">
        <div className={`h-full rounded-full ${bar}`} style={{ width: `${score}%` }} />
      </div>
    </div>
  );
}

// ── CV card inside a JD column ────────────────────────────────────────────────

function CVMappingCard({
  mapping,
  onDelete,
  isDeleting,
  onClick,
}: {
  mapping: CVMappingInJD;
  onDelete: (mappingId: string) => void;
  isDeleting: boolean;
  onClick: () => void;
}) {
  const { t } = useTranslation('ats');
  return (
    <div
      onClick={onClick}
      className="group bg-white rounded-lg border border-gray-200 p-2.5 shadow-sm hover:border-orange-300 hover:shadow-md transition-all cursor-pointer"
    >
      <div className="flex items-start gap-2">
        <User className="h-3.5 w-3.5 text-gray-400 mt-0.5 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium text-gray-800 truncate" title={mapping.cv_name}>
            {mapping.cv_name}
          </p>
          <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
            <StatusBadge status={mapping.analysis_status} />
            {mapping.analysis_status === 'completed' && (
              <ScoreBadge score={mapping.analysis_score} />
            )}
          </div>
        </div>
        <button
          onClick={(e) => { e.stopPropagation(); onDelete(mapping.mapping_id); }}
          disabled={isDeleting}
          className="opacity-0 group-hover:opacity-100 flex-shrink-0 p-1 rounded hover:bg-red-50 text-gray-300 hover:text-red-500 transition-all disabled:cursor-not-allowed"
          title={t('matchingHistory.deleteTitle')}
        >
          {isDeleting ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Trash2 className="h-3.5 w-3.5" />
          )}
        </button>
      </div>
    </div>
  );
}

// ── JD column (mirrors KanbanColumn layout) ───────────────────────────────────

function JDColumn({
  jd,
  onDelete,
  deletingId,
  onCardClick,
}: {
  jd: JDWithMappings;
  onDelete: (mappingId: string) => void;
  deletingId: string | null;
  onCardClick: (state: AnalysisPanelState) => void;
}) {
  const { t } = useTranslation('ats');
  const count = jd.cv_mappings.length;
  const isProcessed = jd.jd_processing_status === 'processed';

  return (
    <div className="flex-shrink-0 w-64 bg-gray-100 rounded-lg flex flex-col h-full">
      {/* Header */}
      <div className="p-3 border-b border-gray-200">
        <div className="flex items-start gap-2">
          <FileText className="h-4 w-4 text-orange-500 mt-0.5 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-medium text-gray-900 truncate leading-snug" title={jd.jd_name}>
              {jd.jd_name}
            </h3>
            <div className="mt-1 flex items-center gap-1.5">
              {isProcessed ? (
                <span className="text-xs text-gray-400">{t('matchingHistory.processed')}</span>
              ) : (
                <span className="inline-flex items-center gap-1 text-xs text-yellow-600">
                  <Loader2 className="h-2.5 w-2.5 animate-spin" />
                  {jd.jd_processing_status}
                </span>
              )}
              <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-gray-200 text-xs font-medium text-gray-700 ml-auto">
                {count}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* CV list */}
      <div className="flex-1 p-2 space-y-2 overflow-y-auto min-h-[120px] scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent">
        {count === 0 ? (
          <div className="flex items-center justify-center h-full text-gray-400 text-xs text-center px-2">
            {t('matchingHistory.noCvsAnalysed')}
          </div>
        ) : (
          jd.cv_mappings.map((m) => (
            <CVMappingCard
              key={m.mapping_id}
              mapping={m}
              onDelete={onDelete}
              isDeleting={deletingId === m.mapping_id}
              onClick={() => onCardClick({
                cvName: m.cv_name,
                jdName: jd.jd_name,
                cvId: m.cv_id,
                jdId: jd.jd_id,
                status: m.analysis_status,
                score: m.analysis_score,
                metadata: m.analysis_metadata,
              })}
            />
          ))
        )}
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

interface Props {
  jobId: string;
  workspaceId: string | undefined;
  idToken: string | undefined;
}

export function MatchingHistoryView({ jobId, workspaceId, idToken }: Props) {
  const { t } = useTranslation('ats');
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [selectedPanel, setSelectedPanel] = useState<AnalysisPanelState | null>(null);
  const search = useDebounce(searchInput, 400);

  const deleteMutation = useDeleteMappingMutation(jobId);
  const retryMutation = useTriggerAnalysisMutation(jobId);

  const handleRetry = async (cvId: string, jdId: string) => {
    await retryMutation.mutateAsync({ cvId, jdId });
    setSelectedPanel(null);
    await refetch();
  };

  const handleDelete = async (mappingId: string) => {
    setDeletingId(mappingId);
    try {
      await deleteMutation.mutateAsync(mappingId);
      await refetch();
    } finally {
      setDeletingId(null);
    }
  };

  const { data, isLoading, refetch } = useJobMappingsByJDQuery({
    jobId,
    workspaceId,
    idToken,
    page,
    pageSize: PAGE_SIZE,
    search,
  });

  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;

  return (
    <div className="flex flex-col h-full gap-3 p-3">
      {/* Toolbar */}
      <div className="flex items-center gap-2 flex-shrink-0">
        <div className="relative flex-1 max-w-64">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
          <input
            type="text"
            placeholder={t('matchingHistory.searchPlaceholder')}
            value={searchInput}
            onChange={(e) => { setSearchInput(e.target.value); setPage(1); }}
            className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-400 bg-white"
          />
        </div>

        {/* Pagination */}
        <div className="flex items-center gap-1.5 ml-auto text-sm text-gray-600">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1 || isLoading}
            className="p-1 rounded hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <span className="tabular-nums text-xs">
            {page} / {totalPages}
            {data && <span className="text-gray-400"> ({data.total})</span>}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages || isLoading}
            className="p-1 rounded hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Columns */}
      <div className="flex-1 min-h-0">
        {isLoading ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="h-6 w-6 text-orange-500 animate-spin" />
          </div>
        ) : !data || data.items.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full gap-2 text-gray-400">
            <FileText className="h-10 w-10 text-gray-200" />
            <p className="text-sm font-medium">{t('matchingHistory.noJdsFound')}</p>
            {search && (
              <p className="text-xs">
                {t('matchingHistory.noJdsMatch', { query: search })}{' '}
                <button
                  className="text-orange-500 hover:underline"
                  onClick={() => { setSearchInput(''); setPage(1); }}
                >
                  {t('matchingHistory.clear')}
                </button>
              </p>
            )}
          </div>
        ) : (
          <div className="flex gap-4 h-full overflow-x-auto pb-2 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent">
            {data.items.map((jd) => (
              <JDColumn
                key={jd.jd_id}
                jd={jd}
                onDelete={handleDelete}
                deletingId={deletingId}
                onCardClick={setSelectedPanel}
              />
            ))}
          </div>
        )}
      </div>

      {selectedPanel && (
        <AnalysisDetailPanel
          {...selectedPanel}
          onClose={() => setSelectedPanel(null)}
          onRetry={handleRetry}
          isRetrying={retryMutation.isPending}
        />
      )}
    </div>
  );
}
